import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowUpRight, BookOpen } from "lucide-react";

type LibrarySource = {
  id: string;
  title: string;
  subtitle: string | null;
  display_title: string | null;
  author_text: string | null;
};

type Props = {
  librarySource: LibrarySource | null;
  returnHref: string | null;
  returnLabel: string | null;
  returnTo: string;
  excerptViewReturnTo: string;
  onClearLibraryItem: () => void;
};

/**
 * "Source context" / "Saved view" card shown above the excerpts list when
 * the user arrived from a My Library item or saved-view link. Extracted
 * from Excerpts.tsx for readability.
 */
export const ExcerptsSourceContextCard = ({
  librarySource,
  returnHref,
  returnLabel,
  returnTo,
  excerptViewReturnTo,
  onClearLibraryItem,
}: Props) => {
  if (!librarySource && !returnHref) return null;
  return (
    <Card className="shadow-soft">
      <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{librarySource ? "Source context" : "Saved view"}</p>
          {librarySource ? (
            <p className="text-sm text-muted-foreground">
              Showing excerpts linked to <span className="text-foreground">{librarySource.display_title || librarySource.title}{librarySource.subtitle ? `: ${librarySource.subtitle}` : ""}</span>
              {librarySource.author_text ? ` — ${librarySource.author_text}` : ""}.
            </p>
          ) : returnLabel ? (
            <p className="text-sm text-muted-foreground">Go back to the originating filtered view without losing your place.</p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {returnHref ? (
            <Link to={returnHref}>
              <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4" /> {returnLabel}</Button>
            </Link>
          ) : null}
          {librarySource ? (
            <>
              <Link to={`/my-library?item=${librarySource.id}&returnTo=${encodeURIComponent(returnTo || excerptViewReturnTo)}`}>
                <Button variant="outline" size="sm"><ArrowUpRight className="h-4 w-4" /> Open source</Button>
              </Link>
              <Link to={`/new-excerpt?libraryItem=${librarySource.id}&returnTo=${encodeURIComponent(excerptViewReturnTo)}`}>
                <Button size="sm"><BookOpen className="h-4 w-4" /> New excerpt</Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={onClearLibraryItem}>
                <ArrowLeft className="h-4 w-4" /> Clear source context
              </Button>
            </>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};
